/**
 * SocketContext - Socket.IO context provider
 *
 * Provides Socket.IO connection and real-time data across the application.
 * Manages connection state, notifications, and real-time updates.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { io } from 'socket.io-client';

type SocketClient = ReturnType<typeof io>;

interface Notification {
  message: string;
  type: 'success' | 'error' | 'info';
  timestamp: number;
}

interface SocketContextType {
  socket: SocketClient | null;
  connected: boolean;
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'timestamp'>) => void;
  clearNotification: (timestamp: number) => void;
}

export const SocketContext = createContext<SocketContextType | null>(null);

interface SocketProviderProps {
  userId: string;
  children: ReactNode;
}

export function SocketProvider({ userId, children }: SocketProviderProps) {
  const [socket, setSocket] = useState<SocketClient | null>(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      timestamp: Date.now(),
    };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.timestamp !== newNotification.timestamp));
    }, 5000);
    console.log(`Notification: [${notification.type}] ${notification.message}`);
  }, []);

  const clearNotification = useCallback((timestamp: number) => {
    setNotifications(prev => prev.filter(n => n.timestamp !== timestamp));
  }, []);

  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000';
    const socketInstance = io(url, {
      query: { userId },
      forceNew: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketInstance.on('connect', () => {
      setConnected(true);
      console.log('Socket connected');
    });

    socketInstance.on('disconnect', () => {
      setConnected(false);
      console.log('Socket disconnected');
    });

    socketInstance.on('notification', (data: Omit<Notification, 'timestamp'>) => {
      addNotification(data);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, [userId, addNotification]);

  const contextValue: SocketContextType = {
    socket,
    connected,
    notifications,
    addNotification,
    clearNotification,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
